import { repositoryFactory } from '@data';

/**
 * Tag Mastery Calculator Service
 * Tracks mastery independently per tag/topic with time-based decay
 * Implements HamTestOnline model: mastery = raw_correct_rate * decay_factor
 */
export class TagMasteryCalculator {
  constructor() {
    this.progressRepository = repositoryFactory.getProgressRepository();
    this.courseRepository = repositoryFactory.getCourseRepository();
  }

  /**
   * Best-effort extraction of the most recent correct timestamp from progress data.
   * Handles both legacy and normalized shapes.
   */
  getLastCorrectDateFromProgress(questionProgress) {
    if (!questionProgress || typeof questionProgress !== 'object') {
      return null;
    }

    const directCandidate = questionProgress.lastCorrectAt || questionProgress.lastCorrectDate;
    if (directCandidate) {
      const parsed = new Date(directCandidate);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    const history = Array.isArray(questionProgress.history) ? questionProgress.history : [];
    let latestHistoryCorrect = null;

    for (const entry of history) {
      const isCorrect = entry?.isCorrect === true || entry?.correct === true;
      if (!isCorrect || !entry?.timestamp) continue;

      const parsed = new Date(entry.timestamp);
      if (Number.isNaN(parsed.getTime())) continue;

      if (!latestHistoryCorrect || parsed > latestHistoryCorrect) {
        latestHistoryCorrect = parsed;
      }
    }

    if (latestHistoryCorrect) {
      return latestHistoryCorrect;
    }

    // Fallback: if correctness count exists but timestamp metadata is absent,
    // avoid forcing mastery to 0 by using the latest known attempt time.
    const timesCorrect = Number(
      questionProgress.timesCorrect ?? questionProgress.correctAttempts ?? 0,
    );
    if (timesCorrect > 0) {
      const attemptCandidate = questionProgress.lastShown || questionProgress.lastAttemptAt;
      if (attemptCandidate) {
        const parsed = new Date(attemptCandidate);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed;
        }
      }

      return new Date();
    }

    return null;
  }

  /**
   * Calculate raw mastery for a tag
   * raw_mastery = correct_count / total_attempts
   */
  async calculateRawTagMastery(userId, courseId, tagId) {
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    const course = await this.courseRepository.getCourseById(courseId);

    if (!progress || !course) {
      return { rawMastery: 0, correctCount: 0, totalCount: 0 };
    }

    // Find all questions with this tag across all sections
    let correctCount = 0;
    let totalCount = 0;

    for (const section of course.sections) {
      const sectionQuestions = await this.courseRepository.getQuestionsForSection(courseId, section.id);
      
      for (const question of sectionQuestions) {
        // Check if question has the tag
        if (question.tags && question.tags.includes(tagId)) {
          const questionProgress = progress.getQuestionProgress(question.id);
          
          if (questionProgress && questionProgress.timesShown > 0) {
            totalCount += questionProgress.timesShown;
            correctCount += questionProgress.timesCorrect || 0;
          }
        }
      }
    }

    const rawMastery = totalCount > 0 ? correctCount / totalCount : 0;

    return {
      rawMastery,
      correctCount,
      totalCount,
      percentage: Math.round(rawMastery * 100),
    };
  }

  /**
   * Calculate time-based decay factor
   * Uses 30-day half-life: decay_factor = exp(-days_since_last_correct / 30)
   */
  calculateDecayFactor(lastCorrectDate, decayHalfLife = 30) {
    if (!lastCorrectDate) {
      return 0; // No attempt yet
    }

    const now = new Date();
    const daysSince = (now - new Date(lastCorrectDate)) / (1000 * 60 * 60 * 24);
    
    // Exponential decay with 30-day half-life
    const decayFactor = Math.exp(-daysSince / decayHalfLife);

    return Math.max(0, Math.min(1, decayFactor)); // Clamp to [0, 1]
  }

  /**
   * Calculate final mastery for a tag (includes decay)
   * final_mastery = raw_mastery * decay_factor
   */
  async calculateTagMastery(userId, courseId, tagId) {
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    const course = await this.courseRepository.getCourseById(courseId);

    if (!progress || !course) {
      return {
        tagId,
        rawMastery: 0,
        decayFactor: 0,
        finalMastery: 0,
        percentage: 0,
        bucket: 'unseen',
        lastCorrectDate: null,
      };
    }

    // Get all questions with this tag
    let correctCount = 0;
    let totalCount = 0;
    let lastCorrectDate = null;

    for (const section of course.sections) {
      const sectionQuestions = await this.courseRepository.getQuestionsForSection(courseId, section.id);
      
      for (const question of sectionQuestions) {
        if (question.tags && question.tags.includes(tagId)) {
          const questionProgress = progress.getQuestionProgress(question.id);
          
          if (questionProgress && questionProgress.timesShown > 0) {
            totalCount += questionProgress.timesShown;
            correctCount += questionProgress.timesCorrect || 0;

            // Track most recent correct answer from normalized or legacy shapes.
            const correctDate = this.getLastCorrectDateFromProgress(questionProgress);
            if (correctDate && (!lastCorrectDate || correctDate > lastCorrectDate)) {
              lastCorrectDate = correctDate;
            }
          }
        }
      }
    }

    const rawMastery = totalCount > 0 ? correctCount / totalCount : 0;
    const decayFactor = this.calculateDecayFactor(lastCorrectDate);
    const finalMastery = rawMastery * decayFactor;
    const bucket = this.getMasteryBucket(finalMastery);

    return {
      tagId,
      rawMastery: Math.round(rawMastery * 100),
      decayFactor: Math.round(decayFactor * 100),
      finalMastery: Math.round(finalMastery * 100),
      percentage: Math.round(finalMastery * 100),
      bucket,
      correctCount,
      totalCount,
      lastCorrectDate,
    };
  }

  /**
   * Get tags due for spaced review
   * Returns tags that are:
   * 1. Previously mastered (70%+ at some point)
   * 2. Now decayed below mastery threshold
   * 3. Need periodic reinforcement to prevent forgetting
   * 
   * Sorted by: (lowest mastery * highest decay) first
   */
  async getTagsDueForSpacedReview(userId, courseId, masteryThreshold = 0.7) {
    const masteries = await this.getAllTagMasteries(userId, courseId);
    
    // Filter tags that have mastery but are below threshold
    // These are "review" bucket items (70-90%) and some "weak" (0-70%) that had mastery
    const dueForReview = masteries.filter(m => {
      // Include review tags (70-90% mastery)
      if (m.bucket === 'review') return true;
      
      // Include weak tags that had attempts (not 'unseen')
      // These had mastery but decayed significantly
      if (m.bucket === 'weak' && m.totalCount > 0) return true;
      
      return false;
    });

    // Sort by (raw_mastery * decay_factor) to find most decayed
    // This prioritizes tags that were known but are now forgotten
    dueForReview.sort((a, b) => {
      const aDecayPriority = (a.rawMastery / 100) * (a.decayFactor / 100);
      const bDecayPriority = (b.rawMastery / 100) * (b.decayFactor / 100);
      return aDecayPriority - bDecayPriority; // Lowest first (most decayed)
    });

    console.log('[TagMasteryCalculator] Tags due for spaced review:', dueForReview.length);
    return dueForReview;
  }

  /**
   * Get sections with decayed mastery for spaced review injection
   * Identifies sections where user has learned content but now shows decay
   * Returns sections sorted by decay priority (most decayed first)
   */
  async getSectionsWithDecayedMastery(userId, courseId, course = null) {
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    if (!progress) return [];

    const courseData = course || await this.courseRepository.getCourseById(courseId);
    if (!courseData) return [];

    const sectionsWithDecay = [];

    for (const section of courseData.sections) {
      const sectionQuestions = await this.courseRepository.getQuestionsForSection(courseId, section.id);
      
      // Collect all tags in this section
      const sectionTags = new Set();
      for (const question of sectionQuestions) {
        if (question.tags && Array.isArray(question.tags)) {
          question.tags.forEach(tag => sectionTags.add(tag));
        }
      }

      if (sectionTags.size === 0) continue; // Skip sections with no tags

      // Calculate average mastery + decay for section
      let totalMastery = 0;
      let totalDecay = 0;
      let tagCount = 0;

      for (const tag of sectionTags) {
        const mastery = await this.calculateTagMastery(userId, courseId, tag);
        totalMastery += mastery.rawMastery / 100;
        totalDecay += mastery.decayFactor / 100;
        tagCount++;
      }

      const avgRawMastery = totalMastery / tagCount;
      const avgDecayFactor = totalDecay / tagCount;
      const avgFinalMastery = avgRawMastery * avgDecayFactor;

      // Include if section was studied (has mastery) but is now decayed
      const sectionProgress = progress.getSectionProgress(section.id);
      if (sectionProgress && sectionProgress.timesStudied > 0) {
        // Include if: has prior study AND (mastery exists OR decay is significant)
        if (avgRawMastery > 0 && avgFinalMastery < 0.8) {
          sectionsWithDecay.push({
            sectionId: section.id,
            sectionTitle: section.title,
            sectionIndex: section.index,
            avgRawMastery: Math.round(avgRawMastery * 100),
            avgDecayFactor: Math.round(avgDecayFactor * 100),
            avgFinalMastery: Math.round(avgFinalMastery * 100),
            tagCount,
            decayPriority: avgRawMastery * (1 - avgDecayFactor), // Higher = more decayed
          });
        }
      }
    }

    // Sort by decay priority (most decayed first)
    sectionsWithDecay.sort((a, b) => b.decayPriority - a.decayPriority);

    console.log('[TagMasteryCalculator] Sections with decayed mastery:', sectionsWithDecay.length);
    return sectionsWithDecay;
  }

  /**
   * Get best section for spaced review from a list of candidate sections
   * Considers: lowest mastery, highest decay, recency
   * 
   * Useful for: selecting which decayed section to inject during learning
   */
  async selectBestSectionForSpacedReview(userId, courseId, candidateSectionIds = null) {
    const decayedSections = await this.getSectionsWithDecayedMastery(userId, courseId);
    
    if (decayedSections.length === 0) return null;

    // Filter by candidates if provided
    let filtered = decayedSections;
    if (candidateSectionIds && candidateSectionIds.length > 0) {
      filtered = decayedSections.filter(s => candidateSectionIds.includes(s.sectionId));
    }

    if (filtered.length === 0) return null;

    // Return most decayed section
    return filtered[0];
  }

  /**
   * Calculate spaced review injection probability
   * Returns likelihood (0-1) that a spaced review should be injected
   * 
   * Based on:
   * - Number of questions answered since last section review
   * - How much mastery has decayed
   * - Course position (inject more often in long courses)
   */
  calculateSpacedReviewProbability(
    questionsSinceLastReview = 0,
    decayFactor = 1.0,
    courseLength = 1
  ) {
    // Base probability increases with questions answered
    // ~5% chance per 10 questions (0.5% per question)
    const baseProbability = Math.min(0.5, questionsSinceLastReview * 0.005);

    // Decay factor increases probability (more decay = higher chance to review)
    // If decay is 0.5 (50% forgotten), increase probability by 2x
    const decayMultiplier = 1 + (1 - decayFactor);

    // Course length multiplier (longer courses need more review)
    // 50 sections vs 5 sections: 10x more reviews needed
    const courseLengthMultiplier = Math.log(courseLength + 1); // ln(51) ≈ 3.9 vs ln(6) ≈ 1.8

    const finalProbability = baseProbability * decayMultiplier * courseLengthMultiplier;

    // Clamp to [0, 1] range
    return Math.min(1, Math.max(0, finalProbability));
  }

  /**
   * Check if should inject spaced review now
   * Deterministic: based on hash of user/course/section to ensure consistent behavior
   */
  shouldInjectSpacedReviewNow(
    userId,
    courseId,
    currentSectionIndex,
    questionsSinceLastReview,
    avgDecayFactor,
    courseLength
  ) {
    const probability = this.calculateSpacedReviewProbability(
      questionsSinceLastReview,
      avgDecayFactor,
      courseLength
    );

    // Create deterministic hash for this context
    // Same user/course/section/question count always gives same result
    const seed = userId.charCodeAt(0) + 
                 courseId.charCodeAt(0) + 
                 currentSectionIndex + 
                 Math.floor(questionsSinceLastReview / 5); // Group by 5 questions

    const random = Math.sin(seed) * 10000;
    const hashValue = random - Math.floor(random);

    const shouldInject = hashValue < probability;

    console.log('[TagMasteryCalculator.shouldInjectSpacedReviewNow]', {
      probability: (probability * 100).toFixed(1) + '%',
      hashValue: (hashValue * 100).toFixed(1) + '%',
      shouldInject,
    });

    return shouldInject;
  }

  /**
   * Get mastery for all tags in course
   */
  async getAllTagMasteries(userId, courseId) {
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    const course = await this.courseRepository.getCourseById(courseId);
    if (!course || !progress) {
      return [];
    }

    const normalizeTagId = (tag) => {
      if (typeof tag === 'string' || typeof tag === 'number') {
        return String(tag).trim();
      }

      if (tag && typeof tag === 'object') {
        const candidate = tag.id ?? tag.tagId ?? tag.value ?? tag.key ?? tag.name;
        return candidate == null ? '' : String(candidate).trim();
      }

      return '';
    };

    const normalizeTagLabel = (tag, fallbackId) => {
      if (typeof tag === 'string' || typeof tag === 'number') {
        return String(tag);
      }

      if (tag && typeof tag === 'object') {
        const candidate = tag.label ?? tag.name ?? fallbackId;
        return candidate == null ? fallbackId : String(candidate);
      }

      return fallbackId;
    };

    // Reuse questions already embedded in getCourseById payload
    const sectionQuestions = course.sections.map(section => section.questions || []);

    // Aggregate metrics by tag in a single pass and include unseen tags.
    const tagStats = new Map();

    for (const questions of sectionQuestions) {
      for (const question of questions || []) {
        if (!question.tags || !Array.isArray(question.tags) || question.tags.length === 0) {
          continue;
        }

        for (const rawTag of question.tags) {
          const tagId = normalizeTagId(rawTag);
          if (!tagId) continue;

          if (!tagStats.has(tagId)) {
            tagStats.set(tagId, {
              tagId,
              tagLabel: normalizeTagLabel(rawTag, tagId),
              correctCount: 0,
              totalCount: 0,
              lastCorrectDate: null,
            });
          }
        }

        const questionProgress = progress.getQuestionProgress(question.id);
        if (!questionProgress || questionProgress.timesShown <= 0) {
          continue;
        }

        for (const rawTag of question.tags) {
          const tagId = normalizeTagId(rawTag);
          if (!tagId || !tagStats.has(tagId)) continue;

          const current = tagStats.get(tagId);
          current.totalCount += questionProgress.timesShown || 0;
          current.correctCount += questionProgress.timesCorrect || 0;

          const correctDate = this.getLastCorrectDateFromProgress(questionProgress);
          if (correctDate && (!current.lastCorrectDate || correctDate > current.lastCorrectDate)) {
            current.lastCorrectDate = correctDate;
          }
        }
      }
    }

    const masteries = Array.from(tagStats.values()).map((stats) => {
      const rawMastery = stats.totalCount > 0 ? stats.correctCount / stats.totalCount : 0;
      const decayFactor = this.calculateDecayFactor(stats.lastCorrectDate);
      const finalMastery = rawMastery * decayFactor;

      return {
        tagId: stats.tagId,
        tagLabel: stats.tagLabel,
        rawMastery: Math.round(rawMastery * 100),
        decayFactor: Math.round(decayFactor * 100),
        finalMastery: Math.round(finalMastery * 100),
        percentage: Math.round(finalMastery * 100),
        bucket: this.getMasteryBucket(finalMastery),
        correctCount: stats.correctCount,
        totalCount: stats.totalCount,
        lastCorrectDate: stats.lastCorrectDate,
      };
    });

    // Sort by final mastery (ascending - weakest first), then by tag label.
    masteries.sort((a, b) => {
      if (a.finalMastery !== b.finalMastery) {
        return a.finalMastery - b.finalMastery;
      }

      return String(a.tagLabel || a.tagId).localeCompare(String(b.tagLabel || b.tagId));
    });

    return masteries;
  }

  /**
   * Get bucket classification for mastery percentage
   * Unseen: 0% | Weak: 0-70% | Review: 70-90% | Learned: 90%+
   */
  getMasteryBucket(mastery) {
    if (mastery === 0) return 'unseen';
    if (mastery < 0.7) return 'weak';
    if (mastery < 0.9) return 'review';
    return 'learned';
  }

  /**
   * Get weak tags (mastery < 70%)
   */
  async getWeakTags(userId, courseId) {
    const masteries = await this.getAllTagMasteries(userId, courseId);
    return masteries.filter(m => m.bucket === 'weak');
  }

  /**
   * Get tags due for review (70-90% mastery)
   */
  async getReviewTags(userId, courseId) {
    const masteries = await this.getAllTagMasteries(userId, courseId);
    return masteries.filter(m => m.bucket === 'review');
  }

  /**
   * Get mastered tags (90%+ mastery)
   */
  async getMasteredTags(userId, courseId) {
    const masteries = await this.getAllTagMasteries(userId, courseId);
    return masteries.filter(m => m.bucket === 'learned');
  }

  /**
   * Check if tag has sufficient mastery
   */
  async isTagMastered(userId, courseId, tagId, threshold = 0.9) {
    const mastery = await this.calculateTagMastery(userId, courseId, tagId);
    return (mastery.finalMastery / 100) >= threshold;
  }

  /**
   * Log tag mastery for debugging
   */
  async logTagMasteries(userId, courseId) {
    const masteries = await this.getAllTagMasteries(userId, courseId);
    
    console.log(`[TagMasteryCalculator] Tag masteries for ${userId}/${courseId}:`);
    masteries.forEach(m => {
      console.log(`  ${m.tagId}: ${m.percentage}% (${m.bucket}) - raw: ${m.rawMastery}%, decay: ${m.decayFactor}%`);
    });

    return masteries;
  }
}

// Export singleton instance
export const tagMasteryCalculator = new TagMasteryCalculator();
